import React, { useState, useEffect, useRef } from "react";
import "./css/InternationalPhoneInput.css";
import { useTheme } from "../context/ThemeContext";

// Phone number formatting functions
const formatPhoneNumber = (digits, countryCode) => {
  if (!digits) return "";

  switch (countryCode) {
    case "US":
    case "CA":
      // Format: (XXX) XXX-XXXX
      if (digits.length <= 3) {
        return `(${digits}`;
      } else if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
          6,
          10,
        )}`;
      }
    case "GB":
      // Format: XXXX XXX XXXX
      if (digits.length <= 4) {
        return digits;
      } else if (digits.length <= 7) {
        return `${digits.slice(0, 4)} ${digits.slice(4)}`;
      } else {
        return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(
          7,
          11,
        )}`;
      }
    case "DE":
    case "FR":
      // Format: XX XX XX XX XX
      return digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
    default:
      // Default: Add spaces every 3 digits
      return digits.replace(/(\d{3})(?=\d)/g, "$1 ").trim();
  }
};

// Remove formatting and return only digits
const stripFormatting = (formatted) => {
  return formatted.replace(/\D/g, "");
};

// Validate phone number for specific country
const validatePhoneNumber = (digits, countryCode) => {
  const country = COUNTRIES.find((c) => c.code === countryCode);
  if (!country) {
    return { isValid: false, message: "Country not supported" };
  }

  const digitCount = digits.length;

  if (digitCount === 0) {
    return { isValid: true, message: "" }; // Empty is valid (user is still typing)
  }

  if (digitCount < country.minDigits) {
    return {
      isValid: false,
      message: `${country.name} phone numbers require at least ${country.minDigits} digits`,
    };
  }

  if (digitCount > country.maxDigits) {
    return {
      isValid: false,
      message: `${country.name} phone numbers cannot exceed ${country.maxDigits} digits`,
    };
  }

  return { isValid: true, message: "" };
};

// Check if phone number is complete for the country
const isPhoneNumberComplete = (digits, countryCode) => {
  const country = COUNTRIES.find((c) => c.code === countryCode);
  if (!country) return false;

  return (
    digits.length >= country.minDigits && digits.length <= country.maxDigits
  );
};

// Country data with validation rules for digit requirements
const COUNTRIES = [
  {
    code: "AF",
    name: "Afghanistan",
    dialCode: "93",
    flag: "🇦🇫",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "AL",
    name: "Albania",
    dialCode: "355",
    flag: "🇦🇱",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "DZ",
    name: "Algeria",
    dialCode: "213",
    flag: "🇩🇿",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "AD",
    name: "Andorra",
    dialCode: "376",
    flag: "🇦🇩",
    minDigits: 6,
    maxDigits: 9,
  },
  {
    code: "AO",
    name: "Angola",
    dialCode: "244",
    flag: "🇦🇴",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "AG",
    name: "Antigua and Barbuda",
    dialCode: "1",
    flag: "🇦🇬",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "AR",
    name: "Argentina",
    dialCode: "54",
    flag: "🇦🇷",
    minDigits: 10,
    maxDigits: 11,
  },
  {
    code: "AM",
    name: "Armenia",
    dialCode: "374",
    flag: "🇦🇲",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "AU",
    name: "Australia",
    dialCode: "61",
    flag: "🇦🇺",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "AT",
    name: "Austria",
    dialCode: "43",
    flag: "🇦🇹",
    minDigits: 7,
    maxDigits: 13,
  },
  {
    code: "AZ",
    name: "Azerbaijan",
    dialCode: "994",
    flag: "🇦🇿",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "BS",
    name: "Bahamas",
    dialCode: "1",
    flag: "🇧🇸",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "BH",
    name: "Bahrain",
    dialCode: "973",
    flag: "🇧🇭",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "BD",
    name: "Bangladesh",
    dialCode: "880",
    flag: "🇧🇩",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "BB",
    name: "Barbados",
    dialCode: "1",
    flag: "🇧🇧",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "BY",
    name: "Belarus",
    dialCode: "375",
    flag: "🇧🇾",
    minDigits: 9,
    maxDigits: 10,
  },
  {
    code: "BE",
    name: "Belgium",
    dialCode: "32",
    flag: "🇧🇪",
    minDigits: 8,
    maxDigits: 9,
  },
  {
    code: "BZ",
    name: "Belize",
    dialCode: "501",
    flag: "🇧🇿",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "BJ",
    name: "Benin",
    dialCode: "229",
    flag: "🇧🇯",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "BT",
    name: "Bhutan",
    dialCode: "975",
    flag: "🇧🇹",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "BO",
    name: "Bolivia",
    dialCode: "591",
    flag: "🇧🇴",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "BA",
    name: "Bosnia and Herzegovina",
    dialCode: "387",
    flag: "🇧🇦",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "BW",
    name: "Botswana",
    dialCode: "267",
    flag: "🇧🇼",
    minDigits: 7,
    maxDigits: 8,
  },
  {
    code: "BR",
    name: "Brazil",
    dialCode: "55",
    flag: "🇧🇷",
    minDigits: 10,
    maxDigits: 11,
  },
  {
    code: "BN",
    name: "Brunei",
    dialCode: "673",
    flag: "🇧🇳",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "BG",
    name: "Bulgaria",
    dialCode: "359",
    flag: "🇧🇬",
    minDigits: 8,
    maxDigits: 9,
  },
  {
    code: "BF",
    name: "Burkina Faso",
    dialCode: "226",
    flag: "🇧🇫",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "BI",
    name: "Burundi",
    dialCode: "257",
    flag: "🇧🇮",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "KH",
    name: "Cambodia",
    dialCode: "855",
    flag: "🇰🇭",
    minDigits: 8,
    maxDigits: 9,
  },
  {
    code: "CM",
    name: "Cameroon",
    dialCode: "237",
    flag: "🇨🇲",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "CA",
    name: "Canada",
    dialCode: "1",
    flag: "🇨🇦",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "CV",
    name: "Cape Verde",
    dialCode: "238",
    flag: "🇨🇻",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "CF",
    name: "Central African Republic",
    dialCode: "236",
    flag: "🇨🇫",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "TD",
    name: "Chad",
    dialCode: "235",
    flag: "🇹🇩",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "CL",
    name: "Chile",
    dialCode: "56",
    flag: "🇨🇱",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "CN",
    name: "China",
    dialCode: "86",
    flag: "🇨🇳",
    minDigits: 11,
    maxDigits: 11,
  },
  {
    code: "CO",
    name: "Colombia",
    dialCode: "57",
    flag: "🇨🇴",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "KM",
    name: "Comoros",
    dialCode: "269",
    flag: "🇰🇲",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "CG",
    name: "Congo",
    dialCode: "242",
    flag: "🇨🇬",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "CD",
    name: "Congo, Dem. Rep.",
    dialCode: "243",
    flag: "🇨🇩",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "CR",
    name: "Costa Rica",
    dialCode: "506",
    flag: "🇨🇷",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "HR",
    name: "Croatia",
    dialCode: "385",
    flag: "🇭🇷",
    minDigits: 8,
    maxDigits: 9,
  },
  {
    code: "CU",
    name: "Cuba",
    dialCode: "53",
    flag: "🇨🇺",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "CY",
    name: "Cyprus",
    dialCode: "357",
    flag: "🇨🇾",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "CZ",
    name: "Czech Republic",
    dialCode: "420",
    flag: "🇨🇿",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "DK",
    name: "Denmark",
    dialCode: "45",
    flag: "🇩🇰",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "DJ",
    name: "Djibouti",
    dialCode: "253",
    flag: "🇩🇯",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "DM",
    name: "Dominica",
    dialCode: "1",
    flag: "🇩🇲",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "DO",
    name: "Dominican Republic",
    dialCode: "1",
    flag: "🇩🇴",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "EC",
    name: "Ecuador",
    dialCode: "593",
    flag: "🇪🇨",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "EG",
    name: "Egypt",
    dialCode: "20",
    flag: "🇪🇬",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "SV",
    name: "El Salvador",
    dialCode: "503",
    flag: "🇸🇻",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "GQ",
    name: "Equatorial Guinea",
    dialCode: "240",
    flag: "🇬🇶",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "ER",
    name: "Eritrea",
    dialCode: "291",
    flag: "🇪🇷",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "EE",
    name: "Estonia",
    dialCode: "372",
    flag: "🇪🇪",
    minDigits: 7,
    maxDigits: 8,
  },
  {
    code: "SZ",
    name: "Eswatini",
    dialCode: "268",
    flag: "🇸🇿",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "ET",
    name: "Ethiopia",
    dialCode: "251",
    flag: "🇪🇹",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "FJ",
    name: "Fiji",
    dialCode: "679",
    flag: "🇫🇯",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "FI",
    name: "Finland",
    dialCode: "358",
    flag: "🇫🇮",
    minDigits: 9,
    maxDigits: 11,
  },
  {
    code: "FR",
    name: "France",
    dialCode: "33",
    flag: "🇫🇷",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "GA",
    name: "Gabon",
    dialCode: "241",
    flag: "🇬🇦",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "GM",
    name: "Gambia",
    dialCode: "220",
    flag: "🇬🇲",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "GE",
    name: "Georgia",
    dialCode: "995",
    flag: "🇬🇪",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "DE",
    name: "Germany",
    dialCode: "49",
    flag: "🇩🇪",
    minDigits: 10,
    maxDigits: 12,
  },
  {
    code: "GH",
    name: "Ghana",
    dialCode: "233",
    flag: "🇬🇭",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "GR",
    name: "Greece",
    dialCode: "30",
    flag: "🇬🇷",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "GD",
    name: "Grenada",
    dialCode: "1",
    flag: "🇬🇩",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "GT",
    name: "Guatemala",
    dialCode: "502",
    flag: "🇬🇹",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "GN",
    name: "Guinea",
    dialCode: "224",
    flag: "🇬🇳",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "GW",
    name: "Guinea-Bissau",
    dialCode: "245",
    flag: "🇬🇼",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "GY",
    name: "Guyana",
    dialCode: "592",
    flag: "🇬🇾",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "HT",
    name: "Haiti",
    dialCode: "509",
    flag: "🇭🇹",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "HN",
    name: "Honduras",
    dialCode: "504",
    flag: "🇭🇳",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "HU",
    name: "Hungary",
    dialCode: "36",
    flag: "🇭🇺",
    minDigits: 8,
    maxDigits: 9,
  },
  {
    code: "IS",
    name: "Iceland",
    dialCode: "354",
    flag: "🇮🇸",
    minDigits: 7,
    maxDigits: 9,
  },
  {
    code: "IN",
    name: "India",
    dialCode: "91",
    flag: "🇮🇳",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "ID",
    name: "Indonesia",
    dialCode: "62",
    flag: "🇮🇩",
    minDigits: 9,
    maxDigits: 12,
  },
  {
    code: "IR",
    name: "Iran",
    dialCode: "98",
    flag: "🇮🇷",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "IQ",
    name: "Iraq",
    dialCode: "964",
    flag: "🇮🇶",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "IE",
    name: "Ireland",
    dialCode: "353",
    flag: "🇮🇪",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "IL",
    name: "Israel",
    dialCode: "972",
    flag: "🇮🇱",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "IT",
    name: "Italy",
    dialCode: "39",
    flag: "🇮🇹",
    minDigits: 9,
    maxDigits: 11,
  },
  {
    code: "CI",
    name: "Ivory Coast",
    dialCode: "225",
    flag: "🇨🇮",
    minDigits: 8,
    maxDigits: 10,
  },
  {
    code: "JM",
    name: "Jamaica",
    dialCode: "1",
    flag: "🇯🇲",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "JP",
    name: "Japan",
    dialCode: "81",
    flag: "🇯🇵",
    minDigits: 10,
    maxDigits: 11,
  },
  {
    code: "JO",
    name: "Jordan",
    dialCode: "962",
    flag: "🇯🇴",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "KZ",
    name: "Kazakhstan",
    dialCode: "7",
    flag: "🇰🇿",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "KE",
    name: "Kenya",
    dialCode: "254",
    flag: "🇰🇪",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "KI",
    name: "Kiribati",
    dialCode: "686",
    flag: "🇰🇮",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "KW",
    name: "Kuwait",
    dialCode: "965",
    flag: "🇰🇼",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "KG",
    name: "Kyrgyzstan",
    dialCode: "996",
    flag: "🇰🇬",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "LA",
    name: "Laos",
    dialCode: "856",
    flag: "🇱🇦",
    minDigits: 8,
    maxDigits: 9,
  },
  {
    code: "LV",
    name: "Latvia",
    dialCode: "371",
    flag: "🇱🇻",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "LB",
    name: "Lebanon",
    dialCode: "961",
    flag: "🇱🇧",
    minDigits: 7,
    maxDigits: 8,
  },
  {
    code: "LS",
    name: "Lesotho",
    dialCode: "266",
    flag: "🇱🇸",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "LR",
    name: "Liberia",
    dialCode: "231",
    flag: "🇱🇷",
    minDigits: 7,
    maxDigits: 8,
  },
  {
    code: "LY",
    name: "Libya",
    dialCode: "218",
    flag: "🇱🇾",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "LI",
    name: "Liechtenstein",
    dialCode: "423",
    flag: "🇱🇮",
    minDigits: 7,
    maxDigits: 9,
  },
  {
    code: "LT",
    name: "Lithuania",
    dialCode: "370",
    flag: "🇱🇹",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "LU",
    name: "Luxembourg",
    dialCode: "352",
    flag: "🇱🇺",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "MG",
    name: "Madagascar",
    dialCode: "261",
    flag: "🇲🇬",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "MW",
    name: "Malawi",
    dialCode: "265",
    flag: "🇲🇼",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "MY",
    name: "Malaysia",
    dialCode: "60",
    flag: "🇲🇾",
    minDigits: 9,
    maxDigits: 10,
  },
  {
    code: "MV",
    name: "Maldives",
    dialCode: "960",
    flag: "🇲🇻",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "ML",
    name: "Mali",
    dialCode: "223",
    flag: "🇲🇱",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "MT",
    name: "Malta",
    dialCode: "356",
    flag: "🇲🇹",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "MH",
    name: "Marshall Islands",
    dialCode: "692",
    flag: "🇲🇭",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "MR",
    name: "Mauritania",
    dialCode: "222",
    flag: "🇲🇷",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "MU",
    name: "Mauritius",
    dialCode: "230",
    flag: "🇲🇺",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "MX",
    name: "Mexico",
    dialCode: "52",
    flag: "🇲🇽",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "FM",
    name: "Micronesia",
    dialCode: "691",
    flag: "🇫🇲",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "MD",
    name: "Moldova",
    dialCode: "373",
    flag: "🇲🇩",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "MC",
    name: "Monaco",
    dialCode: "377",
    flag: "🇲🇨",
    minDigits: 8,
    maxDigits: 9,
  },
  {
    code: "MN",
    name: "Mongolia",
    dialCode: "976",
    flag: "🇲🇳",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "ME",
    name: "Montenegro",
    dialCode: "382",
    flag: "🇲🇪",
    minDigits: 8,
    maxDigits: 9,
  },
  {
    code: "MA",
    name: "Morocco",
    dialCode: "212",
    flag: "🇲🇦",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "MZ",
    name: "Mozambique",
    dialCode: "258",
    flag: "🇲🇿",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "MM",
    name: "Myanmar",
    dialCode: "95",
    flag: "🇲🇲",
    minDigits: 8,
    maxDigits: 10,
  },
  {
    code: "NA",
    name: "Namibia",
    dialCode: "264",
    flag: "🇳🇦",
    minDigits: 9,
    maxDigits: 10,
  },
  {
    code: "NR",
    name: "Nauru",
    dialCode: "674",
    flag: "🇳🇷",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "NP",
    name: "Nepal",
    dialCode: "977",
    flag: "🇳🇵",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "NL",
    name: "Netherlands",
    dialCode: "31",
    flag: "🇳🇱",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "NZ",
    name: "New Zealand",
    dialCode: "64",
    flag: "🇳🇿",
    minDigits: 8,
    maxDigits: 10,
  },
  {
    code: "NI",
    name: "Nicaragua",
    dialCode: "505",
    flag: "🇳🇮",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "NE",
    name: "Niger",
    dialCode: "227",
    flag: "🇳🇪",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "NG",
    name: "Nigeria",
    dialCode: "234",
    flag: "🇳🇬",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "KP",
    name: "North Korea",
    dialCode: "850",
    flag: "🇰🇵",
    minDigits: 9,
    maxDigits: 10,
  },
  {
    code: "MK",
    name: "North Macedonia",
    dialCode: "389",
    flag: "🇲🇰",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "NO",
    name: "Norway",
    dialCode: "47",
    flag: "🇳🇴",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "OM",
    name: "Oman",
    dialCode: "968",
    flag: "🇴🇲",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "PK",
    name: "Pakistan",
    dialCode: "92",
    flag: "🇵🇰",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "PW",
    name: "Palau",
    dialCode: "680",
    flag: "🇵🇼",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "PS",
    name: "Palestine",
    dialCode: "970",
    flag: "🇵🇸",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "PA",
    name: "Panama",
    dialCode: "507",
    flag: "🇵🇦",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "PG",
    name: "Papua New Guinea",
    dialCode: "675",
    flag: "🇵🇬",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "PY",
    name: "Paraguay",
    dialCode: "595",
    flag: "🇵🇾",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "PE",
    name: "Peru",
    dialCode: "51",
    flag: "🇵🇪",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "PH",
    name: "Philippines",
    dialCode: "63",
    flag: "🇵🇭",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "PL",
    name: "Poland",
    dialCode: "48",
    flag: "🇵🇱",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "PT",
    name: "Portugal",
    dialCode: "351",
    flag: "🇵🇹",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "QA",
    name: "Qatar",
    dialCode: "974",
    flag: "🇶🇦",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "RO",
    name: "Romania",
    dialCode: "40",
    flag: "🇷🇴",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "RU",
    name: "Russia",
    dialCode: "7",
    flag: "🇷🇺",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "RW",
    name: "Rwanda",
    dialCode: "250",
    flag: "🇷🇼",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "KN",
    name: "Saint Kitts and Nevis",
    dialCode: "1",
    flag: "🇰🇳",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "LC",
    name: "Saint Lucia",
    dialCode: "1",
    flag: "🇱🇨",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "VC",
    name: "Saint Vincent and the Grenadines",
    dialCode: "1",
    flag: "🇻🇨",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "WS",
    name: "Samoa",
    dialCode: "685",
    flag: "🇼🇸",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "SM",
    name: "San Marino",
    dialCode: "378",
    flag: "🇸🇲",
    minDigits: 6,
    maxDigits: 10,
  },
  {
    code: "ST",
    name: "Sao Tome and Principe",
    dialCode: "239",
    flag: "🇸🇹",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "SA",
    name: "Saudi Arabia",
    dialCode: "966",
    flag: "🇸🇦",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "SN",
    name: "Senegal",
    dialCode: "221",
    flag: "🇸🇳",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "RS",
    name: "Serbia",
    dialCode: "381",
    flag: "🇷🇸",
    minDigits: 8,
    maxDigits: 9,
  },
  {
    code: "SC",
    name: "Seychelles",
    dialCode: "248",
    flag: "🇸🇨",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "SL",
    name: "Sierra Leone",
    dialCode: "232",
    flag: "🇸🇱",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "SG",
    name: "Singapore",
    dialCode: "65",
    flag: "🇸🇬",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "SK",
    name: "Slovakia",
    dialCode: "421",
    flag: "🇸🇰",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "SI",
    name: "Slovenia",
    dialCode: "386",
    flag: "🇸🇮",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "SB",
    name: "Solomon Islands",
    dialCode: "677",
    flag: "🇸🇧",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "SO",
    name: "Somalia",
    dialCode: "252",
    flag: "🇸🇴",
    minDigits: 7,
    maxDigits: 8,
  },
  {
    code: "ZA",
    name: "South Africa",
    dialCode: "27",
    flag: "🇿🇦",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "KR",
    name: "South Korea",
    dialCode: "82",
    flag: "🇰🇷",
    minDigits: 9,
    maxDigits: 10,
  },
  {
    code: "SS",
    name: "South Sudan",
    dialCode: "211",
    flag: "🇸🇸",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "ES",
    name: "Spain",
    dialCode: "34",
    flag: "🇪🇸",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "LK",
    name: "Sri Lanka",
    dialCode: "94",
    flag: "🇱🇰",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "SD",
    name: "Sudan",
    dialCode: "249",
    flag: "🇸🇩",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "SR",
    name: "Suriname",
    dialCode: "597",
    flag: "🇸🇷",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "SE",
    name: "Sweden",
    dialCode: "46",
    flag: "🇸🇪",
    minDigits: 7,
    maxDigits: 9,
  },
  {
    code: "CH",
    name: "Switzerland",
    dialCode: "41",
    flag: "🇨🇭",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "SY",
    name: "Syria",
    dialCode: "963",
    flag: "🇸🇾",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "TW",
    name: "Taiwan",
    dialCode: "886",
    flag: "🇹🇼",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "TJ",
    name: "Tajikistan",
    dialCode: "992",
    flag: "🇹🇯",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "TZ",
    name: "Tanzania",
    dialCode: "255",
    flag: "🇹🇿",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "TH",
    name: "Thailand",
    dialCode: "66",
    flag: "🇹🇭",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "TL",
    name: "Timor-Leste",
    dialCode: "670",
    flag: "🇹🇱",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "TG",
    name: "Togo",
    dialCode: "228",
    flag: "🇹🇬",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "TO",
    name: "Tonga",
    dialCode: "676",
    flag: "🇹🇴",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "TT",
    name: "Trinidad and Tobago",
    dialCode: "1",
    flag: "🇹🇹",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "TN",
    name: "Tunisia",
    dialCode: "216",
    flag: "🇹🇳",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "TR",
    name: "Turkey",
    dialCode: "90",
    flag: "🇹🇷",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "TM",
    name: "Turkmenistan",
    dialCode: "993",
    flag: "🇹🇲",
    minDigits: 8,
    maxDigits: 8,
  },
  {
    code: "TV",
    name: "Tuvalu",
    dialCode: "688",
    flag: "🇹🇻",
    minDigits: 6,
    maxDigits: 6,
  },
  {
    code: "UG",
    name: "Uganda",
    dialCode: "256",
    flag: "🇺🇬",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "UA",
    name: "Ukraine",
    dialCode: "380",
    flag: "🇺🇦",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "AE",
    name: "United Arab Emirates",
    dialCode: "971",
    flag: "🇦🇪",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "GB",
    name: "United Kingdom",
    dialCode: "44",
    flag: "🇬🇧",
    minDigits: 10,
    maxDigits: 11,
  },
  {
    code: "US",
    name: "United States",
    dialCode: "1",
    flag: "🇺🇸",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "UY",
    name: "Uruguay",
    dialCode: "598",
    flag: "🇺🇾",
    minDigits: 8,
    maxDigits: 9,
  },
  {
    code: "UZ",
    name: "Uzbekistan",
    dialCode: "998",
    flag: "🇺🇿",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "VU",
    name: "Vanuatu",
    dialCode: "678",
    flag: "🇻🇺",
    minDigits: 7,
    maxDigits: 7,
  },
  {
    code: "VE",
    name: "Venezuela",
    dialCode: "58",
    flag: "🇻🇪",
    minDigits: 10,
    maxDigits: 10,
  },
  {
    code: "VN",
    name: "Vietnam",
    dialCode: "84",
    flag: "🇻🇳",
    minDigits: 9,
    maxDigits: 10,
  },
  {
    code: "YE",
    name: "Yemen",
    dialCode: "967",
    flag: "🇾🇪",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "ZM",
    name: "Zambia",
    dialCode: "260",
    flag: "🇿🇲",
    minDigits: 9,
    maxDigits: 9,
  },
  {
    code: "ZW",
    name: "Zimbabwe",
    dialCode: "263",
    flag: "🇿🇼",
    minDigits: 9,
    maxDigits: 9,
  },
].sort((a, b) => a.name.localeCompare(b.name));

// Top 20 priority country codes always shown without searching
const PRIORITY_CODES = new Set([
  "US",
  "CA",
  "GB",
  "FR",
  "DE",
  "IT",
  "ES",
  "AU",
  "JP",
  "CN",
  "IN",
  "BR",
  "MX",
  "RU",
  "KR",
  "NL",
  "SE",
  "NO",
  "CH",
  "BE",
]);

const InternationalPhoneInput = ({
  value = "",
  onChange,
  error = "",
  disabled = false,
  className = "",
  placeholder = "Enter phone number",
  isValid = true, // New prop for validation state
  onKeyPress, // Support for keypress events
  showValidation = true, // Show validation messages
  clearValidation = false, // New prop to clear validation externally
  ...inputProps // Pass through any additional input props
}) => {
  const [selectedCountry, setSelectedCountry] = useState(
    COUNTRIES.find((c) => c.code === "US"),
  );
  const [phoneNumber, setPhoneNumber] = useState(""); // Raw digits only - saved to database
  const [formattedPhone, setFormattedPhone] = useState(""); // Formatted display value - UI only
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [validationState, setValidationState] = useState({
    isValid: true,
    message: "",
  });
  const containerRef = useRef(null);

  // Theme context
  const { effectiveTheme } = useTheme();

  // Parse incoming E.164 value
  useEffect(() => {
    if (value && value.startsWith("+")) {
      // Find all countries with matching dial code (handles US/Canada both having +1)
      const matchingCountries = COUNTRIES.filter((c) =>
        value.startsWith(`+${c.dialCode}`),
      );

      if (matchingCountries.length > 0) {
        // If only one match, use it
        let country = matchingCountries[0];

        // If multiple countries share the dial code (like US and Canada with +1),
        // prefer the currently selected country if it's one of the matches
        if (
          matchingCountries.length > 1 &&
          matchingCountries.some((c) => c.code === selectedCountry.code)
        ) {
          country = matchingCountries.find(
            (c) => c.code === selectedCountry.code,
          );
        }

        setSelectedCountry(country);
        const digits = value.substring(country.dialCode.length + 1);
        setPhoneNumber(digits);
        setFormattedPhone(formatPhoneNumber(digits, country.code));
      }
    } else if (!value) {
      // Clear the input if no value
      setPhoneNumber("");
      setFormattedPhone("");
      // Clear validation state when input is cleared
      setValidationState({
        isValid: true,
        message: "",
      });
    }
  }, [value]);

  // Update formatted phone when country changes
  useEffect(() => {
    if (phoneNumber) {
      setFormattedPhone(formatPhoneNumber(phoneNumber, selectedCountry.code));
    }
  }, [selectedCountry, phoneNumber]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Clear validation when clearValidation prop changes
  useEffect(() => {
    if (clearValidation) {
      setValidationState({
        isValid: true,
        message: "",
      });
    }
  }, [clearValidation]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setDropdownSearch("");

    // Reformat existing phone number for new country
    if (phoneNumber) {
      setFormattedPhone(formatPhoneNumber(phoneNumber, country.code));

      // Re-validate with new country
      const validation = validatePhoneNumber(phoneNumber, country.code);
      setValidationState(validation);
    }

    // Update parent with new E.164 format
    const newE164 = phoneNumber
      ? `+${country.dialCode}${phoneNumber}`
      : `+${country.dialCode}`;

    if (onChange) {
      onChange(newE164, {
        isValid: phoneNumber
          ? validatePhoneNumber(phoneNumber, country.code).isValid
          : true,
        isComplete: phoneNumber
          ? isPhoneNumberComplete(phoneNumber, country.code)
          : false,
        country: country,
        digits: phoneNumber,
      });
    }
  };

  const handlePhoneNumberChange = (e) => {
    const inputValue = e.target.value;

    // Extract only digits from input (removing any existing formatting)
    const digits = stripFormatting(inputValue);

    // Limit digits based on country max digits
    const maxDigits = selectedCountry.maxDigits || 15;
    const limitedDigits = digits.slice(0, maxDigits);

    // Validate the phone number
    const validation = validatePhoneNumber(limitedDigits, selectedCountry.code);
    setValidationState(validation);

    // Update raw digits (this is what gets saved to database)
    setPhoneNumber(limitedDigits);

    // Update formatted display (this is ONLY for user display)
    const formatted = formatPhoneNumber(limitedDigits, selectedCountry.code);
    setFormattedPhone(formatted);

    // IMPORTANT: Always send raw digits to parent/database in E.164 format
    // Example: User sees "(301) 237-4581" but database gets "+13012374581"
    const newE164 = limitedDigits
      ? `+${selectedCountry.dialCode}${limitedDigits}`
      : `+${selectedCountry.dialCode}`;

    // Include validation info when calling onChange
    if (onChange) {
      onChange(newE164, {
        isValid: validation.isValid,
        isComplete: isPhoneNumberComplete(limitedDigits, selectedCountry.code),
        country: selectedCountry,
        digits: limitedDigits,
      });
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleClearPhone = () => {
    // Clear all phone-related states
    setPhoneNumber("");
    setFormattedPhone("");
    setValidationState({
      isValid: true,
      message: "",
    });

    // Send clear signal to parent with just the country code
    const clearedE164 = `+${selectedCountry.dialCode}`;
    if (onChange) {
      onChange(clearedE164, {
        isValid: true,
        isComplete: false,
        country: selectedCountry,
        digits: "",
      });
    }
  };

  return (
    <div
      ref={containerRef}
      className={`international-phone-input ${className}`}
      data-theme={effectiveTheme}
    >
      <div className="phone-input-wrapper">
        {/* Country selector button */}
        <button
          type="button"
          className="country-selector"
          onClick={toggleDropdown}
          disabled={disabled}
        >
          <span className="flag">{selectedCountry.flag}</span>
          <span className="dial-code">+{selectedCountry.dialCode}</span>
          <span className="arrow">{isDropdownOpen ? "▲" : "▼"}</span>
        </button>

        {/* Phone number input */}
        <input
          type="tel"
          value={formattedPhone}
          onChange={handlePhoneNumberChange}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          className="phone-input"
          style={{
            backgroundColor:
              (formattedPhone && !validationState.isValid) ||
              (formattedPhone && !isValid)
                ? "#ffebee"
                : undefined, // Let CSS theme styles handle the default background
          }}
          {...inputProps}
        />

        {/* Clear button */}
        {formattedPhone && !disabled && (
          <button
            type="button"
            className="clear-button"
            onClick={handleClearPhone}
            tabIndex={-1}
            aria-label="Clear phone number"
            title="Clear phone number"
          >
            ×
          </button>
        )}
      </div>

      {/* Dropdown list */}
      {isDropdownOpen && (
        <div className="country-dropdown">
          <div className="country-search">
            <input
              type="text"
              className="country-search-input"
              placeholder="Search countries..."
              value={dropdownSearch}
              onChange={(e) => setDropdownSearch(e.target.value)}
              autoFocus
            />
          </div>
          {(() => {
            const isSearching = dropdownSearch.trim().length > 0;
            const term = dropdownSearch.toLowerCase();
            if (isSearching) {
              return COUNTRIES.filter(
                (c) =>
                  c.name.toLowerCase().includes(term) ||
                  c.code.toLowerCase().includes(term) ||
                  c.dialCode.includes(term),
              ).map((country) => (
                <button
                  key={country.code}
                  type="button"
                  className={`country-option ${
                    selectedCountry.code === country.code ? "selected" : ""
                  }`}
                  onClick={() => handleCountrySelect(country)}
                >
                  <span className="flag">{country.flag}</span>
                  <span className="country-name">{country.name}</span>
                  <span className="dial-code">+{country.dialCode}</span>
                </button>
              ));
            }
            const priority = COUNTRIES.filter((c) =>
              PRIORITY_CODES.has(c.code),
            );
            const others = COUNTRIES.filter((c) => !PRIORITY_CODES.has(c.code));
            return (
              <>
                {priority.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    className={`country-option ${
                      selectedCountry.code === country.code ? "selected" : ""
                    }`}
                    onClick={() => handleCountrySelect(country)}
                  >
                    <span className="flag">{country.flag}</span>
                    <span className="country-name">{country.name}</span>
                    <span className="dial-code">+{country.dialCode}</span>
                  </button>
                ))}
                <div className="country-divider">All countries</div>
                {others.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    className={`country-option ${
                      selectedCountry.code === country.code ? "selected" : ""
                    }`}
                    onClick={() => handleCountrySelect(country)}
                  >
                    <span className="flag">{country.flag}</span>
                    <span className="country-name">{country.name}</span>
                    <span className="dial-code">+{country.dialCode}</span>
                  </button>
                ))}
              </>
            );
          })()}
        </div>
      )}

      {/* Error messages */}
      {error && <div className="error-message">{error}</div>}
      {showValidation &&
        !validationState.isValid &&
        validationState.message && (
          <div className="validation-message">{validationState.message}</div>
        )}
    </div>
  );
};

export default InternationalPhoneInput;

// Export validation functions for external use
export { validatePhoneNumber, isPhoneNumberComplete, COUNTRIES };
